import {route} from '@react-router/dev/routes'
import {SupportedLanguage} from './locales'
import {getRootPath} from './url'

export const Routes = {
  TRANSLATE: {
    name: 'nav_pages.translate',
    path: '/translate',
    element: 'routes/translate/route.tsx'
  },
  SALES: {
    name: 'nav_pages.sales',
    path: '/sales',
    element: 'routes/sales/route.tsx'
  },
  ABOUT: {
    name: 'nav_pages.about',
    path: '/about-lara',
    element: 'routes/about/route.tsx'
  },
  LARA_EVALUATION: {
    name: 'lara-evaluation.page_name',
    path: '/lara-evaluation',
    element: 'routes/lara-evaluation/route.tsx'
  },
  PRIVACY_POLICY: {
    name: 'privacy-policy.page_name',
    path: '/privacy-policy',
    element: 'routes/privacy-policy/route.tsx'
  },
  TERMS_AND_CONDITIONS: {
    name: 'terms-and-conditions.page_name',
    path: '/terms-and-conditions',
    element: 'routes/terms-and-conditions/route.tsx'
  }
} as const

export const Layouts = {
  LANDING: {
    name: 'landing:layout_name',
    element: 'layouts/landing/LandingLayout.tsx'
  },
  STANDARD: {
    name: 'standard:layout_name',
    element: 'layouts/standard/StandardLayout.tsx'
  }
}

export type RoutePaths = (typeof Routes)[keyof typeof Routes]
export type LayoutsPaths = (typeof Layouts)[keyof typeof Layouts]

export const localizedPaths = {
  '/translate': {
    // Tier 1
    en: '/translate',
    de: '/übersetzer',
    es: '/traductor',
    fr: '/traduction',
    it: '/traduttore',
    ja: '/翻訳',
    ko: '/번역기',
    'pt-BR': '/tradutor',
    ru: '/переводчик',
    'zh-CN': '/翻译',
    'zh-TW': '/翻譯',

    // Tier 2
    ar: '/ترجمة',
    bg: '/преводач',
    ca: '/traductor-ca',
    cs: '/překladač',
    da: '/oversæt',
    el: '/μετάφραση',
    fi: '/kääntäjä',
    he: '/תרגום',
    hr: '/prevoditelj',
    hu: '/fordító',
    id: '/terjemahan',
    ms: '/terjemahan-ms',
    nb: '/oversetter',
    nl: '/vertalen',
    pl: '/tłumacz',
    sk: '/prekladač',
    sv: '/översätt',
    th: '/แปลภาษา',
    tr: '/çeviri',
    uk: '/перекладач'
  },

  '/about-lara': {
    // Tier 1
    en: '/about-lara',
    de: '/über-lara',
    es: '/acerca-de-lara',
    fr: '/découvrez-lara',
    it: '/scopri-lara',
    ja: '/ララについて',
    ko: '/라라-소개',
    'pt-BR': '/descubra-lara',
    ru: '/о-lara',
    'zh-CN': '/关于-lara',
    'zh-TW': '/關於-lara',

    // Tier 2
    ar: '/حول-lara',
    bg: '/за-lara',
    ca: '/sobre-lara',
    cs: '/info-lara',
    da: '/info-om-lara',
    el: '/σχετικά-με-τη-lara',
    fi: '/tietoa-larasta',
    he: '/אודות-lara',
    hr: '/o-lari',
    hu: '/lara-bemutatása',
    id: '/tentang-lara',
    ms: '/tentang-lara-ms',
    nb: '/oppdag-lara',
    nl: '/over-lara',
    pl: '/o-lara',
    sk: '/o-lare',
    sv: '/om-lara',
    th: '/เกี่ยวกับ-lara',
    tr: '/hakkında-lara',
    uk: '/про-lara'
  },

  '/lara-evaluation': {
    // Tier 1
    en: '/lara-evaluation',
    de: '/lara-bewertung',
    es: '/lara-evaluación',
    fr: '/lara-évaluation',
    it: '/lara-valutazione',
    ja: '/lara-評価',
    ko: '/lara-평가',
    'pt-BR': '/lara-avaliação',
    ru: '/lara-оценка',
    'zh-CN': '/lara-评估',
    'zh-TW': '/lara-評估',

    // Tier 2
    ar: '/تقييم-lara',
    bg: '/оценка-на-lara',
    ca: '/avaluació-lara',
    cs: '/hodnocení-lara',
    da: '/lara-evaluering',
    el: '/αξιολόγηση-lara',
    fi: '/lara-arviointi',
    he: '/הערכת-lara',
    hr: '/lara-procjena',
    hu: '/lara-értékelés',
    id: '/evaluasi-lara',
    ms: '/penilaian-lara-ms',
    nb: '/lara-vurdering',
    nl: '/lara-evaluatie',
    pl: '/ocena-lara',
    sk: '/hodnotenie-lara',
    sv: '/lara-utvärdering',
    th: '/การประเมิน-lara',
    tr: '/lara-değerlendirme',
    uk: '/оцінка-lara'
  },

  '/sales': {
    // Tier 1
    en: '/sales',
    de: '/vertrieb',
    es: '/ventas',
    fr: '/ventes',
    it: '/vendite',
    ja: '/営業',
    ko: '/영업',
    'pt-BR': '/vendas',
    ru: '/продажи',
    'zh-CN': '/销售',
    'zh-TW': '/銷售',

    // Tier 2
    ar: '/المبيعات',
    bg: '/продажби',
    ca: '/vendes',
    cs: '/prodej',
    da: '/salg',
    el: '/πωλήσεις',
    fi: '/myynti',
    he: '/מכירות',
    hr: '/prodaja',
    hu: '/értékesítés',
    id: '/penjualan',
    ms: '/jualan-ms',
    nb: '/lara-salg',
    nl: '/verkoop',
    pl: '/sprzedaż',
    sk: '/predaj',
    sv: '/försäljning',
    th: '/การขาย',
    tr: '/satış',
    uk: '/продажі'
  },

  '/terms-and-conditions': {
    // Tier 1
    en: '/terms-and-conditions',
    de: '/nutzungsbedingungen',
    es: '/terminos-y-condiciones',
    fr: '/conditions-generales',
    it: '/termini-e-condizioni',
    ja: '/規約と条件',
    ko: '/약관및조건',
    'pt-BR': '/termos-e-condicoes',
    ru: '/условия-и-положения',
    'zh-CN': '/条款和条件',
    'zh-TW': '/條款和條件',

    // Tier 2
    ar: '/الشروط-والأحكام',
    bg: '/условия-за-ползване',
    ca: '/termes-i-condicions',
    cs: '/obchodní-podmínky',
    da: '/vilkår-og-betingelser',
    el: '/όροι-και-προϋποθέσεις',
    fi: '/käyttöehdot',
    he: '/תנאים-והגבלות',
    hr: '/uvjeti-i-odredbe',
    hu: '/feltételek',
    id: '/syarat-dan-ketentuan',
    ms: '/terma-dan-syarat-ms',
    nb: '/vilkår-og-betingelser-b',
    nl: '/algemene-voorwaarden',
    pl: '/warunki-i-zasady',
    sk: '/obchodné-podmienky',
    sv: '/allmänna-villkor',
    th: '/ข้อกำหนดและเงื่อนไข',
    tr: '/şartlar-ve-koşullar',
    uk: '/умови-та-положення'
  },

  '/privacy-policy': {
    // Tier 1
    en: '/privacy-policy',
    de: '/datenschutzrichtlinie',
    es: '/politica-de-privacidad',
    fr: '/politique-de-confidentialite',
    it: '/politica-sulla-privacy',
    ja: '/プライバシーポリシー',
    ko: '/개인정보처리방침',
    'pt-BR': '/politica-de-privacidade',
    ru: '/политика-конфиденциальности',
    'zh-CN': '/隐私政策',
    'zh-TW': '/隱私政策',

    // Tier 2
    ar: '/سياسة-الخصوصية',
    bg: '/политика-за-поверителност',
    ca: '/política-de-privacitat',
    cs: '/zásady-ochrany-osobních-údajů',
    da: '/privatlivspolitik',
    el: '/πολιτική-απορρήτου',
    fi: '/tietosuojakäytäntö',
    he: '/מדיניות-פרטיות',
    hr: '/pravila-privatnosti',
    hu: '/adatvédelmi-irányelvek',
    id: '/kebijakan-privasi',
    ms: '/dasar-privasi-ms',
    nb: '/personvernpolicy',
    nl: '/privacybeleid',
    pl: '/polityka-prywatności',
    sk: '/zásady-ochrany-súkromia',
    sv: '/integritetspolicy',
    th: '/นโยบายความเป็นส่วนตัว',
    tr: '/gizlilik-politikası',
    uk: '/політика-конфіденційності'
  }
}

export const localizedPathToPathMap: Record<string, WebsitePath> = {}
for (const [path, translations] of Object.entries(localizedPaths) as Array<
  [keyof typeof localizedPaths, Record<string, string>]
>) {
  for (const translation of Object.values(translations)) {
    const decodedPath = decodeURIComponent(translation)
    localizedPathToPathMap[translation] = path
    localizedPathToPathMap[decodedPath] = path
  }
}

export type WebsitePath = keyof typeof localizedPaths

export const detectLanguageFromPath = (
  path: string,
  pathToPathMap: Record<string, string>,
  localizedPaths: Record<string, Record<string, string>>
): SupportedLanguage | null => {
  const decodedPath = decodeURIComponent(path)
  const basePath = pathToPathMap[decodedPath]
  if (!basePath) return null

  const translations = localizedPaths[basePath]
  const language = Object.entries(translations).find(
    ([, translatedPath]) => translatedPath === decodedPath
  )?.[0]

  if (!language) return null

  return language as SupportedLanguage
}

export const detectLanguageFromWebsitePath = (path: string): SupportedLanguage | null => {
  return detectLanguageFromPath(path, localizedPathToPathMap, localizedPaths)
}

export const getLocalizedPath = ({
  path,
  language
}: {
  path: WebsitePath
  language: SupportedLanguage
}) => {
  const localizedPath = localizedPaths[path]

  if (localizedPath && language in localizedPath) {
    return decodeURIComponent(localizedPath[language])
  }

  return undefined
}

export const getPathFromLocalizedPath = (path: string) => {
  const normalizedPath = decodeURIComponent(path).replace(/\/$/, '')
  return localizedPathToPathMap[normalizedPath]
}

export const getPathDataFromRequest = (request: Request) => {
  const pathname = new URL(request.url).pathname
  const localizedPath = getRootPath(pathname)
  const currentPath = getPathFromLocalizedPath(localizedPath)
  const language = detectLanguageFromWebsitePath(localizedPath) || 'en'

  return {
    pathname,
    currentPath,
    language
  }
}

export const makeLocalizedRoutes = (routeConfig: RoutePaths) => {
  return Object.entries(localizedPaths[routeConfig.path]).map(([lang, path]) =>
    route(path, routeConfig.element, {id: `routes${routeConfig.path}/${lang}`})
  )
}

// NOTE: Add back if needed
// export const makeTranslateRoutes = () => {
//   return makeLocalizedRoutes(Routes.TRANSLATE).map(localizedRoute => ({
//     ...localizedRoute,
//     children: [
//       route(`${localizedRoute.path}/:langPair`, Routes.TRANSLATE.element, {
//         id: `routes/${detectLanguageFromWebsitePath(localizedRoute.path || '')}/langPair`
//       })
//     ]
//   }))
// }
